import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { SopService } from '../service/core/sop.service';
import { AppDatePipe } from '../service/core/app-date.pipe';

export interface MyTaskCard {
  caseId: string;
  workflowName: string;
  createdAt: string;
  totalTasks: number;
  completedTasks: number;
  waId: string;
}

@Component({
  selector: 'app-my-task',
  templateUrl: './my-task.html',
  styleUrl: './my-task.scss',
  standalone: true,
  imports: [
    CommonModule, AppDatePipe, FormsModule,
    ButtonModule, InputTextModule,
    ProgressSpinnerModule, TooltipModule, RouterModule
  ]
})
export class MyTask implements OnInit {
  searchTerm = '';
  loading = false;
  loadError = false;

  pending: MyTaskCard[] = [];
  inProgress: MyTaskCard[] = [];
  completed: MyTaskCard[] = [];

  constructor(private sopService: SopService, private router: Router) {}

  ngOnInit(): void {
    this.loadMyTasks();
  }

  loadMyTasks(): void {
    this.loading = true;
    this.loadError = false;

    this.sopService.getMyTasks().subscribe({
      next: (res: any) => {
        this.loading = false;
        const active: any[] = res?.data?.active ?? [];
        const done: any[] = res?.data?.completed ?? [];

        const mapCard = (c: any): MyTaskCard => ({
          caseId:         c.caseId ?? '',
          workflowName:   c.workflowName ?? '',
          createdAt:      c.createdAt ?? '',
          totalTasks:     c.totalTasks ?? 0,
          completedTasks: c.completedTasks ?? 0,
          waId:           c.waId ?? c.waid ?? '',
        });

        const mapped = active.map(mapCard);
        this.pending    = mapped.filter(c => c.completedTasks === 0);
        this.inProgress = mapped.filter(c => c.completedTasks > 0);
        this.completed  = done.map(mapCard);
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      }
    });
  }

  openCase(card: MyTaskCard): void {
    const params: Record<string, string> = { caseId: card.caseId, hideSidebar: 'true', fromTask: 'true' };
    if (card.waId) params['waId'] = card.waId;
    this.router.navigate(['/conversations'], { queryParams: params });
  }

  get filteredPending(): MyTaskCard[] {
    return this.applySearch(this.pending);
  }

  get filteredInProgress(): MyTaskCard[] {
    return this.applySearch(this.inProgress);
  }

  get filteredCompleted(): MyTaskCard[] {
    return this.applySearch(this.completed);
  }

  private applySearch(cards: MyTaskCard[]): MyTaskCard[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return cards;
    return cards.filter(c =>
      c.caseId.toLowerCase().includes(term) ||
      c.workflowName.toLowerCase().includes(term)
    );
  }

  get totalCount(): number {
    return this.pending.length + this.inProgress.length + this.completed.length;
  }

  progressPercent(card: MyTaskCard): number {
    if (!card.totalTasks) return 0;
    return Math.round((card.completedTasks / card.totalTasks) * 100);
  }
}
